import React, { useEffect, useMemo, useState } from "react";
import { DesktopIcon } from "../../components/DesktopIcon";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MdDragIndicator } from "react-icons/md";

const ICON_WIDTH = 74;
const ICON_HEIGHT = 110; 
const ICON_GAP = 16;

const VISIBLE_ROWS = 5; 
const HEADER_HEIGHT = 44;

const STORAGE_KEY = "desktop-grid-layout-v5";

const saveLayout = (data: any) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
};

const loadLayout = () => {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
};

const DraggableGroupWrapper = ({ id, group, groupItems, scrollAreaHeight, query, onOpen }: any) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: id,
    data: { type: 'group', group }
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    zIndex: isDragging ? 999 : 1,
    height: '100%'
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        style={{
          width: '100%',
          height: scrollAreaHeight + HEADER_HEIGHT + 80, // Dinamik yükseklik hesabı
          borderRadius: 28,
          padding: 24,
          background: "rgba(255,255,255,0.45)",
          backdropFilter: "blur(30px)",
          boxShadow: "0 20px 50px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.6)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          border: "1px solid rgba(255,255,255,0.4)"
        }}
      >
        {/* HEADER AREA (DRAG HANDLE) */}
        <div
          {...attributes}
          {...listeners}
          style={{
            height: HEADER_HEIGHT,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
            background: "linear-gradient(90deg, rgba(241, 245, 249, 0.95), rgba(226, 232, 240, 0.8))",
            padding: "0 16px",
            borderRadius: 14,
            cursor: "grab",
            border: "1px solid rgba(203, 213, 225, 0.4)",
            boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <MdDragIndicator size={22} color="#64748B" />
            <div
              style={{
                fontSize: 13,
                fontWeight: 900,
                color: "#1E293B",
                textTransform: "uppercase",
                letterSpacing: "1px"
              }}
            >
              {group}
            </div>
          </div>

          <div style={{ fontSize: 11, fontWeight: 900, background: "#334155", color: "#fff", padding: "3px 10px", borderRadius: 8, boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}>
            {(groupItems || []).length}
          </div>
        </div>

        <DroppableArea id={id}>
          <div
            className="custom-scrollbar"
            style={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              paddingRight: 10,
              marginTop: 4
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(auto-fill, minmax(${ICON_WIDTH}px, 1fr))`,
                columnGap: ICON_GAP,
                rowGap: 24,
                justifyContent: "center",
                padding: "10px 4px 40px 4px"
              }}
            >
              {(groupItems || [])
                .filter((i: any) => {
                  const q = query.trim().toLowerCase();
                  if (!q) return true;
                  return (i?.label ?? "").toString().toLowerCase().includes(q);
                })
                .map((item: any) => (
                  <DraggableIcon key={item.key} item={item}>
                    <DesktopIcon item={item} onOpen={onOpen} prod={item.prod} />
                  </DraggableIcon>
                ))}
            </div>
          </div>
        </DroppableArea>
      </div>
    </div>
  );
};

const DroppableArea = ({ id, children }: any) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{
        flex: 1,
        borderRadius: 16,
        background: isOver ? "rgba(59, 130, 246, 0.05)" : "transparent",
        transition: "all 0.3s ease",
        display: "flex",
        flexDirection: "column"
      }}
    >
      {children}
    </div>
  );
};

const DraggableIcon = ({ item, children }: any) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.key,
    data: { type: 'icon', item },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${isDragging ? 1.08 : 1})` : undefined,
        opacity: isDragging ? 0.3 : 1,
        cursor: "grab",
        touchAction: "none",
        zIndex: isDragging ? 2000 : 1,
        display: "flex",
        justifyContent: "center"
      }}
    >
      {children}
    </div>
  );
};

export const DesktopGrid = ({ items, onOpen }: any) => {
  const [query, setQuery] = useState("");
  const [activeItem, setActiveItem] = useState<any>(null);
  const [activeType, setActiveType] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 10 } }));

  const [groupState, setGroupState] = useState<any>(null);
  const [groupOrder, setGroupOrder] = useState<string[]>([]);

  useEffect(() => {
    const saved = loadLayout();
    const map = new Map<string, any[]>();
    
    (items || []).forEach((item: any) => {
      const g = item.group ?? "Other";
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(item);
    });

    const entries = Object.fromEntries(map.entries());
    
    if (saved) {
      if (saved.order) setGroupOrder(saved.order);
      else {
        setGroupOrder(Object.keys(entries).sort((a,b) => {
          if(a === "Aviation") return -1;
          if(b === "Aviation") return 1;
          return a.localeCompare(b);
        }));
      }

      const next: any = {};
      Object.keys(entries).forEach(g => next[g] = []);

      Object.keys(saved.groups || {}).forEach(g => {
        if (!next[g]) next[g] = [];
        (saved.groups[g] || []).forEach((key: string) => {
          const original = items.find((i: any) => i.key === key);
          if (original) next[g].push(original);
        });
      });

      const usedKeys = new Set(Object.values(saved.groups || {}).flat() as string[]);
      items.forEach((i: any) => {
        if (!usedKeys.has(i.key)) {
          const g = i.group ?? "Other";
          if (!next[g]) next[g] = [];
          next[g].push(i);
        }
      });
      setGroupState(next);
    } else {
      setGroupState(entries);
      setGroupOrder(Object.keys(entries).sort((a,b) => {
        if(a === "Aviation") return -1;
        if(b === "Aviation") return 1;
        return a.localeCompare(b);
      }));
    }
  }, [items]);

  const scrollAreaHeight = VISIBLE_ROWS * ICON_HEIGHT + (VISIBLE_ROWS - 1) * 20;

  const onDragStart = (event: any) => {
    const data = event.active.data.current;
    setActiveType(data?.type);
    if (data?.type === 'icon') setActiveItem(data.item);
    else if (data?.type === 'group') setActiveItem({ label: data.group });
  };

  const onDragEnd = (event: any) => {
    const { active, over } = event;
    setActiveItem(null);
    setActiveType(null);

    if (!over) return;

    const activeData = active.data.current;
    if (activeData?.type === 'group') {
      if (active.id !== over.id) {
        setGroupOrder((prev) => {
          const oldIndex = prev.indexOf(active.id);
          const newIndex = prev.indexOf(over.id);
          const next = arrayMove(prev, oldIndex, newIndex);
          saveLayout({ order: next, groups: persistHelper(groupState) });
          return next;
        });
      }
    } else if (activeData?.type === 'icon') {
      const sourceKey = active.id;
      const toGroup = over.id;
      
      const fromGroup = Object.keys(groupState).find(g => groupState[g].some((i: any) => i.key === sourceKey));
      if (!fromGroup || fromGroup === toGroup) return;

      setGroupState((prev: any) => {
        const next = { ...prev };
        const item = next[fromGroup].find((i: any) => i.key === sourceKey);
        next[fromGroup] = next[fromGroup].filter((i: any) => i.key !== sourceKey);
        next[toGroup] = [...(next[toGroup] || []), item];
        saveLayout({ order: groupOrder, groups: persistHelper(next) });
        return next;
      });
    }
  };

  const persistHelper = (state: any) => {
    const res: any = {};
    Object.keys(state || {}).forEach(g => res[g] = state[g].map((i: any) => i.key));
    return res;
  };

  if (!groupState) return null;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.12); border-radius: 10px; border: 2px solid transparent; background-clip: content-box; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.25); background-clip: content-box; }
      `}</style>

      <div style={{ position: "absolute", inset: 0, padding: "20px 40px 60px 40px", overflowY: "auto", overflowX: "hidden" }}>
        {/* SEARCH BAR */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 40, marginTop: 10 }}>
          <div style={{ width: "100%", maxWidth: 600, height: 48, borderRadius: 999, background: "rgba(255,255,255,0.95)", backdropFilter: "blur(20px)", display: "flex", alignItems: "center", padding: "0 20px", boxShadow: "0 8px 24px rgba(0,0,0,0.06)", border: "1px solid rgba(255,255,255,0.6)" }}>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search for anything…" style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 14, fontWeight: 700, color: "#1E293B" }} />
            {query && ( <div onClick={() => setQuery("")} style={{ width: 22, height: 22, borderRadius: "50%", background: "#94A3B8", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 12 }}> × </div> )}
          </div>
        </div>

        {/* RESPONSIVE GRID CONTAINER */}
        <div 
          style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 380px), 1fr))", 
            gap: 32, 
            width: "100%",
            maxWidth: 1800,
            margin: "0 auto" 
          }}
        >
          <SortableContext items={groupOrder} strategy={horizontalListSortingStrategy}>
            {groupOrder.map((group) => (
              <DraggableGroupWrapper
                key={group}
                id={group}
                group={group}
                groupItems={groupState[group]}
                scrollAreaHeight={scrollAreaHeight}
                query={query}
                onOpen={onOpen}
              />
            ))}
          </SortableContext>
        </div>
      </div>

      <DragOverlay dropAnimation={{ duration: 250, easing: "cubic-bezier(0.18, 0.89, 0.32, 1.28)" }}>
        {activeItem ? (
          activeType === 'icon' ? (
            <div style={{ transform: "scale(1.15)", opacity: 0.95, pointerEvents: "none" }}>
              <DesktopIcon item={activeItem} onOpen={onOpen} prod={activeItem.prod} />
            </div>
          ) : (
            <div style={{ width: 400, height: 60, borderRadius: 16, background: "rgba(255,255,255,0.98)", backdropFilter: "blur(15px)", border: "1px solid #CBD5E1", display: "flex", alignItems: "center", padding: "0 24px", fontWeight: 900, fontSize: 14, color: "#0F172A", textTransform: "uppercase", boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}>
               <MdDragIndicator size={20} style={{ marginRight: 12 }} /> {activeItem.label}
            </div>
          )
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
